import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import {
    CircularProgress,
    Box,
    Typography,
    Tabs,
    Tab,
    AppBar,
    Button,
    Alert,
    AlertTitle,
    Tooltip,
    IconButton,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DOMPurify from 'dompurify';
import Papa from 'papaparse';
import config from '../utils/Constants';
import FeedbackWidget from './FeedbackWidget';
import { useRegenerateSectionMutation } from '../redux/medchartApi';

const MAX_REGENS = 3;
const SUPPORT_EMAIL = 'support@bacclabs.io';

function AdmissionDetails() {
    const { id } = useParams();
    const [accessToken, setAccessToken] = useState('');
    const [detailsData, setDetailsData] = useState([]);
    const [groupedDetails, setGroupedDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    const apiUrl = config.getApiUrl();

    const [activeTab, setActiveTab] = useState(0);
    const [tabLabels, setTabLabels] = useState([]);

    // Regen counts keyed by "admissionId:sectionLabel", persisted in localStorage
    const getRegenKey = (section) => `regenCount:${id}:${section}`;
    const getRegenCount = (section) =>
        parseInt(localStorage.getItem(getRegenKey(section)) || '0', 10);

    const [regenCounts, setRegenCounts] = useState({});
    const [serverRegenCount, setServerRegenCount] = useState(null);
    const [regeneratingSection, setRegeneratingSection] = useState(null);
    const [regenError, setRegenError] = useState(null);

    // RTK Query mutation for section regeneration (story-007)
    const [triggerRegenerate] = useRegenerateSectionMutation();

    // Fetch access token on component mount
    useEffect(() => {
        getAccessToken();
    }, []);

    // Fetch admission details when access token is available
    useEffect(() => {
        if (accessToken) {
            fetchAdmissionDetails();
        }
    }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load regen counts from localStorage whenever tabLabels change
    useEffect(() => {
        if (tabLabels.length === 0) return;
        const counts = {};
        tabLabels.forEach((label) => {
            counts[label] = getRegenCount(label);
        });
        setRegenCounts(counts);
    }, [tabLabels]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Access Token
    const getAccessToken = async () => {
        try {
            const session = await fetchAuthSession();
            const token = session.tokens.accessToken.toString();
            setAccessToken(token);
        } catch (e) {
            console.error('Error fetching access token:', e);
            setError(e);
        }
    };

    // Fetch Admission Details
    const fetchAdmissionDetails = async () => {
        setLoadingDetails(true);
        setError(null);

        try {
            const response = await fetch(
                `${apiUrl}/api/catalog/admissions/${id}/details?format=html`,
                {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const jsonData = await response.json();

            // Support both array response and object response with regen_count
            const details = Array.isArray(jsonData) ? jsonData : (jsonData.details ?? jsonData.results ?? []);
            const serverCount = jsonData.regen_count ?? null;
            if (serverCount !== null) {
                setServerRegenCount(serverCount);
            }

            const grouped = details.reduce((acc, detail) => {
                const { detail_type } = detail;
                if (!acc[detail_type]) acc[detail_type] = [];
                acc[detail_type].push(detail);
                return acc;
            }, {});

            setDetailsData(details);
            setGroupedDetails(grouped);
            setTabLabels(Object.keys(grouped));
        } catch (err) {
            console.error('Error fetching admission details:', err);
            setError(err);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Regenerate a section (story-007: uses RTK Query mutation)
    const regenerateSection = async (sectionLabel) => {
        if (!accessToken) return;
        if (regeneratingSection) return; // guard concurrent regen clicks (Fix 4)

        // Server-driven limit check is primary; localStorage is secondary
        const atLimit = (serverRegenCount ?? 0) >= MAX_REGENS;
        const localCount = getRegenCount(sectionLabel);
        if (atLimit || localCount >= MAX_REGENS) return;

        setRegeneratingSection(sectionLabel);
        setRegenError(null);

        try {
            await triggerRegenerate({
                admissionId: id,
                detailType: sectionLabel,
            }).unwrap(); // regen succeeded

            try {
                await fetchAdmissionDetails(); // try to re-fetch
            } catch {
                setRegenError('Section regenerated — refresh the page to see updated content.');
            }

            const newCount = localCount + 1;
            localStorage.setItem(getRegenKey(sectionLabel), String(newCount));
            setRegenCounts((prev) => ({ ...prev, [sectionLabel]: newCount }));
        } catch (err) {
            console.error('Error regenerating section:', err);
            setRegenError(err?.message || 'Could not regenerate this section.');
        } finally {
            setRegeneratingSection(null);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const extractTextFromHTML = (htmlString) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        return tempDiv.textContent || tempDiv.innerText || '';
    };

    const exportToCSV = () => {
        const csvData = detailsData.map((detail) => ({
            DetailType: detail.detail_type,
            Key: detail.key,
            Value: extractTextFromHTML(detail.value),
        }));
        const csv = Papa.unparse(csvData, { header: true });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute('download', `admission_details_${id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div>
            <Typography variant="h4" gutterBottom>
                Admission Details for ID: {id}
            </Typography>
            <Button
                variant="contained"
                color="primary"
                onClick={exportToCSV}
                style={{ marginBottom: '20px' }}
                disabled={detailsData.length === 0}
            >
                Export Details to CSV
            </Button>

            {loadingDetails ? (
                <Box display="flex" justifyContent="center" mt={2}>
                    <CircularProgress />
                    <Box ml={2}>Loading admission details...</Box>
                </Box>
            ) : error ? (
                <Box color="error.main" mt={2}>
                    Error: {error.message}
                </Box>
            ) : detailsData.length === 0 ? (
                <Box mt={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress />
                    <Typography variant="h6">Generating patient record</Typography>
                    <Typography variant="body2" color="text.secondary" align="center" maxWidth={480}>
                        Our backend is synthesizing clinical data for this admission. This typically
                        takes 1-3 minutes. Refresh the page in a moment to see the completed record.
                    </Typography>
                </Box>
            ) : (
                <>
                    <AppBar position="static" color="default">
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {tabLabels.map((label, index) => (
                                <Tab key={index} label={label} />
                            ))}
                        </Tabs>
                    </AppBar>

                    <Box p={2}>
                        {tabLabels.map((label, index) => (
                            <div
                                key={index}
                                role="tabpanel"
                                hidden={activeTab !== index}
                                id={`tabpanel-${index}`}
                                aria-labelledby={`tab-${index}`}
                            >
                                {activeTab === index && (
                                    <div>
                                        {groupedDetails[label].map((detail, idx) => (
                                            <div key={idx} style={{ marginBottom: '10px' }}>
                                                <Typography variant="subtitle1">{detail.key}</Typography>
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(detail.value),
                                                    }}
                                                    style={{ border: '1px solid #ccc', padding: '10px' }}
                                                />
                                            </div>
                                        ))}

                                        {/* ↻ Regenerate section button (story-007) */}
                                        <Box mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                            {(serverRegenCount ?? regenCounts[label] ?? 0) < MAX_REGENS ? (
                                                <>
                                                    <Tooltip
                                                        title={
                                                            'Regenerate this section (' +
                                                            (MAX_REGENS - (regenCounts[label] ?? 0)) +
                                                            ' of ' + MAX_REGENS + ' remaining)'
                                                        }
                                                    >
                                                        <span>
                                                            <Button
                                                                variant="outlined"
                                                                color="secondary"
                                                                size="small"
                                                                onClick={() => regenerateSection(label)}
                                                                disabled={regeneratingSection === label}
                                                                startIcon={
                                                                    regeneratingSection === label
                                                                        ? <CircularProgress size={14} />
                                                                        : <AutorenewIcon fontSize="small" />
                                                                }
                                                                aria-label={'Regenerate ' + label + ' section'}
                                                            >
                                                                {regeneratingSection === label
                                                                    ? 'Regenerating...'
                                                                    : 'Regenerate'}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>

                                                    {regenError && regeneratingSection !== label && (
                                                        <Alert severity="error" style={{ padding: '2px 8px', marginTop: '4px' }}>
                                                            {regenError}
                                                        </Alert>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip title="Max regenerations reached">
                                                        <span>
                                                            <IconButton
                                                                disabled
                                                                size="small"
                                                                aria-label="Regenerate section (disabled)"
                                                            >
                                                                <AutorenewIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Alert severity="warning" style={{ marginTop: '8px' }}>
                                                        <AlertTitle>Regeneration limit reached</AlertTitle>
                                                        You have used all {MAX_REGENS} regenerations for this section.
                                                        Need more?{' '}
                                                        <a href={'mailto:' + SUPPORT_EMAIL}>Contact support</a>
                                                        {' '}and we will get you sorted.
                                                    </Alert>
                                                </>
                                            )}
                                        </Box>
                                    </div>
                                )}
                            </div>
                        ))}
                    </Box>

                    {/* Feedback panel — extracted component handles its own localStorage gate */}
                    <FeedbackWidget admissionId={id} />
                </>
            )}
        </div>
    );
}

export default AdmissionDetails;
